// Fixed extension -> content-type table (§3.4, §3.7). This is the SERVING type (the MASL entry's
// `content-type`) and is extension-only: the served bytes are fixed by the extension, not by
// environment-dependent server sniffing. Unknown extensions map to `application/octet-stream`. Types
// are emitted bare (no `; charset=…`) so the bytes are fixed.
// (Distinct from the blob-ref `mimeType` in sniff.ts, which reproduces the PDS's deterministic
// magic-byte sniff so the stored blob and its ref agree; see sniff.ts.)
//
// This table is a NORMATIVE vector artifact (§C.3a): changing an entry changes root CIDs.
// `.js` -> `application/javascript` matches the spec's worked example (Appendix D).

const TABLE: Record<string, string> = {
  // markup / text
  html: "text/html",
  htm: "text/html",
  css: "text/css",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  xml: "application/xml",
  // scripts / data
  js: "application/javascript",
  mjs: "application/javascript",
  cjs: "application/javascript",
  json: "application/json",
  map: "application/json",
  webmanifest: "application/manifest+json",
  wasm: "application/wasm",
  pdf: "application/pdf",
  // images
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  svg: "image/svg+xml",
  ico: "image/x-icon",
  bmp: "image/bmp",
  // fonts
  woff: "font/woff",
  woff2: "font/woff2",
  ttf: "font/ttf",
  otf: "font/otf",
  eot: "application/vnd.ms-fontobject",
  // media
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  mp4: "video/mp4",
  webm: "video/webm",
};

export const DEFAULT_CONTENT_TYPE = "application/octet-stream";

/** Derive a resource's content-type from its path extension, deterministically (§3.4). */
export function contentTypeFor(path: string): string {
  const slash = path.lastIndexOf("/");
  const base = slash === -1 ? path : path.slice(slash + 1);
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return DEFAULT_CONTENT_TYPE; // no extension, or dotfile like `.gitignore`
  const ext = base.slice(dot + 1).toLowerCase();
  return TABLE[ext] ?? DEFAULT_CONTENT_TYPE;
}
